import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Task, TaskStatus, Priority, TaskCategory } from '../types';
import TaskCard from '../components/TaskCard';
import DailyStats from '../components/DailyStats';
import EmptyState from '../components/EmptyState';

const TODAY = new Date().toISOString().slice(0, 10);

const TOMORROW = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
})();

function sevenDaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

function formatDateHeader(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const todayStr = TODAY;
  const yesterdayStr = (() => {
    const y = new Date(); y.setDate(y.getDate() - 1);
    return y.toISOString().slice(0, 10);
  })();
  const tomorrowStr = TOMORROW;

  const label =
    dateStr === todayStr ? 'Today — ' :
    dateStr === yesterdayStr ? 'Yesterday — ' :
    dateStr === tomorrowStr ? 'Tomorrow — ' : '';

  return label + d.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

function shiftDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function Tasks() {
  const { user, role } = useAuth();
  const { showToast } = useToast();

  const [selectedDate, setSelectedDate] = useState(TODAY);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [weekTasks, setWeekTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'All'>('All');
  const [priorityFilter, setPriorityFilter] = useState<Priority | 'All'>('All');
  const [categoryFilter, setCategoryFilter] = useState<TaskCategory | 'All'>('All');
  const [search, setSearch] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(true);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const notifiedRef = useRef<Set<string>>(new Set());

  const isToday = selectedDate === TODAY;

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Carry over pending tasks from previous days to today (only runs on initial load)
  const carryOver = useCallback(async () => {
    if (!user) return;
    if (role !== 'developer') return;
    try {
      await api.post<{ carried: number }>('/api/tasks/carry-forward', {});
    } catch {
      // Non-fatal — proceed to fetch tasks regardless
    }
  }, [role, user]);

  // Push all incomplete tasks from selectedDate to tomorrow
  const pushToTomorrow = async () => {
    if (!user || pushing) return;
    const incomplete = tasks.filter(t => t.status !== 'Completed');
    if (incomplete.length === 0) { showToast('No incomplete tasks to push.', 'error'); return; }
    setPushing(true);
    let count = 0;
    for (const t of incomplete) {
      try {
        await api.patch<Task>('/api/tasks/' + t.id, {
          task_date: TOMORROW,
          carried_over: true,
          original_date: t.original_date ?? t.task_date,
        });
        count++;
      } catch {
        // continue with remaining tasks
      }
    }
    await fetchTasks();
    setPushing(false);
    showToast(`${count} task${count !== 1 ? 's' : ''} pushed to tomorrow.`, 'success');
  };

  const fetchTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      const [dateData, weekData] = await Promise.all([
        api.get<Task[]>(`/api/tasks?task_date=${selectedDate}`),
        api.get<Task[]>(`/api/tasks?date_from=${sevenDaysAgo()}`),
      ]);

      const order: Record<string, number> = { High: 0, Medium: 1, Low: 2 };
      const sOrder: Record<string, number> = { 'In Progress': 0, Pending: 1, Completed: 2 };
      const sorted = [...dateData].sort((a, b) =>
        order[a.priority] - order[b.priority] || sOrder[a.status] - sOrder[b.status]
      );
      setTasks(sorted);
      setWeekTasks(weekData);
    } catch {
      showToast('Failed to load tasks.', 'error');
    }

    setLoading(false);
  }, [user, selectedDate, showToast]);

  // On first load: carry over then fetch
  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      carryOver().then(fetchTasks);
    } else {
      fetchTasks();
    }
  }, [carryOver, fetchTasks]);

  // Deadline notifications: check every minute (only meaningful for today)
  useEffect(() => {
    if (!isToday) return;
    const check = () => {
      tasks.forEach(task => {
        if (!task.deadline || task.status === 'Completed') return;
        const minsLeft = Math.floor((new Date(task.deadline).getTime() - Date.now()) / 60000);
        if (minsLeft > 0 && minsLeft <= 60 && !notifiedRef.current.has(task.id)) {
          notifiedRef.current.add(task.id);
          showToast(`⏰ "${task.title}" is due in ${minsLeft} minute${minsLeft === 1 ? '' : 's'}!`, 'error');
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Task Deadline Approaching', {
              body: `"${task.title}" is due in ${minsLeft} minute${minsLeft === 1 ? '' : 's'}!`,
            });
          }
        }
      });
    };
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, [tasks, showToast, isToday]);

  const handleStatusChange = async (id: string, status: TaskStatus, progress: number) => {
    const wasCompleted = status === 'Completed';
    const patch: Partial<Task> = { status, progress };
    if (wasCompleted) patch.completed_at = new Date().toISOString();

    try {
      const data = await api.patch<Task>('/api/tasks/' + id, patch);

      setTasks(prev => {
        const order: Record<string, number> = { High: 0, Medium: 1, Low: 2 };
        const sOrder: Record<string, number> = { 'In Progress': 0, Pending: 1, Completed: 2 };
        const updated = prev.map(t => t.id === id ? data : t);
        return updated.sort((a, b) =>
          order[a.priority] - order[b.priority] || sOrder[a.status] - sOrder[b.status]
        );
      });

      if (wasCompleted) {
        const newCompleted = tasks.filter(t => t.status === 'Completed').length + 1;
        const total = tasks.length;
        if (newCompleted === total) {
          showToast('🎉 Amazing! You completed ALL tasks for today!', 'success');
        } else {
          showToast(`✅ Task complete! Well done! (${newCompleted}/${total} done)`, 'success');
        }
      }

      if (wasCompleted) {
        const task = tasks.find(t => t.id === id);
        if (task && task.recurrence !== 'none') {
          const nextDate = new Date(task.task_date);
          if (task.recurrence === 'daily') nextDate.setDate(nextDate.getDate() + 1);
          else if (task.recurrence === 'weekly') nextDate.setDate(nextDate.getDate() + 7);
          await api.post<Task>('/api/tasks', {
            user_id: task.assigned_developer_id ?? user!.id,
            company_id: task.company_id,
            created_by: task.created_by,
            assigned_developer_id: task.assigned_developer_id,
            assigned_tester_id: task.assigned_tester_id,
            review_status: task.review_status,
            title: task.title,
            description: task.description,
            priority: task.priority,
            status: 'Pending',
            category: task.category,
            progress: 0,
            task_date: nextDate.toISOString().slice(0, 10),
            recurrence: task.recurrence,
            deadline: null,
          });
        }
      }
    } catch (err: any) {
      showToast(err?.message || 'Failed to update task.', 'error');
    }
  };

  const goToDate = (date: string) => {
    setSelectedDate(date);
    setStatusFilter('All');
    setPriorityFilter('All');
    setCategoryFilter('All');
    setSearch('');
  };

  const carriedOver = tasks.filter(t => t.carried_over);
  const filtered = tasks.filter(t => {
    if (statusFilter !== 'All' && t.status !== statusFilter) return false;
    if (priorityFilter !== 'All' && t.priority !== priorityFilter) return false;
    if (categoryFilter !== 'All' && t.category !== categoryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!t.title.toLowerCase().includes(q) && !t.description?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const completed = tasks.filter(t => t.status === 'Completed').length;
  const total = tasks.length;

  return (
    <div className="page tasks-page">
      {/* Header */}
      <div className="tasks-header">
        <div>
          <p className="task-date-display">{formatDateHeader(selectedDate)}</p>
          <h1 className="page-title">Daily Tasks</h1>
          <p className="page-subtitle">{role === 'admin' ? 'Admin view of company tasks' : role === 'tester' ? 'Review and verify completed tasks' : 'Assigned tasks dashboard'}</p>
          {total > 0 && (
            <p className="page-subtitle">
              {completed}/{total} completed
              {completed === total && total > 0 && ' 🎉'}
            </p>
          )}
        </div>
        <div className="tasks-header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => setShowAnalytics(!showAnalytics)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            {showAnalytics ? 'Hide Analytics' : 'Analytics'}
          </button>
          <Link to="/tasks/reports" className="btn btn-secondary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
            </svg>
            Reports
          </Link>
          <Link to="/tasks/create" className="btn btn-primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Task
          </Link>
        </div>
      </div>

      {/* Date navigation bar */}
      <div className="date-nav-bar">
        <button
          className="date-nav-btn"
          onClick={() => goToDate(shiftDate(selectedDate, -1))}
          title="Previous day"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <button
          className="date-nav-date-btn"
          onClick={() => dateInputRef.current?.showPicker?.()}
          title="Pick a date"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span className="date-nav-date-text">
            {selectedDate === TODAY ? 'Today' :
             selectedDate === shiftDate(TODAY, -1) ? 'Yesterday' :
             selectedDate === TOMORROW ? 'Tomorrow' :
             new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </span>
          <input
            ref={dateInputRef}
            type="date"
            value={selectedDate}
            max={TOMORROW}
            onChange={e => { if (e.target.value) goToDate(e.target.value); }}
            className="date-nav-hidden-input"
          />
        </button>

        <button
          className="date-nav-btn"
          onClick={() => goToDate(shiftDate(selectedDate, 1))}
          disabled={selectedDate >= TOMORROW}
          title="Next day"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div className="date-nav-divider" />

        {/* Quick-jump chips */}
        <div className="date-nav-chips">
          {!isToday && (
            <button className="date-nav-chip today-chip" onClick={() => goToDate(TODAY)}>
              Today
            </button>
          )}
          <button
            className={`date-nav-chip ${selectedDate === shiftDate(TODAY, -1) ? 'active' : ''}`}
            onClick={() => goToDate(shiftDate(TODAY, -1))}
          >
            Yesterday
          </button>
          {[2, 3, 4, 5, 6].map(n => {
            const d = shiftDate(TODAY, -n);
            const label = new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            return (
              <button
                key={n}
                className={`date-nav-chip ${selectedDate === d ? 'active' : ''}`}
                onClick={() => goToDate(d)}
                title={d}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Carry-over banner — only visible when viewing today */}
      {isToday && carriedOver.length > 0 && (
        <div className="carried-banner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          <span style={{ flex: 1 }}>
            <strong>{carriedOver.length} unfinished task{carriedOver.length > 1 ? 's' : ''}</strong> carried over from a previous day — finish them today!
          </span>
          <button
            className="btn btn-secondary btn-sm carried-push-btn"
            onClick={pushToTomorrow}
            disabled={pushing}
            title="Push all incomplete tasks to tomorrow instead"
          >
            {pushing ? <><span className="btn-spinner" /> Pushing…</> : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                  <polyline points="5 12 19 12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
                Push to Tomorrow
              </>
            )}
          </button>
        </div>
      )}

      {/* Analytics */}
      {showAnalytics && (
        <DailyStats
          todayTasks={tasks}
          weekTasks={weekTasks}
          activeStatus={statusFilter}
          onStatusClick={setStatusFilter}
        />
      )}

      {/* Filters */}
      <div className="task-filter-bar">
        <div className="filter-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text" placeholder="Search tasks…"
            value={search} onChange={e => setSearch(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="task-filter-chips">
          <div className="filter-group">
            <span className="filter-label">Status</span>
            <div className="filter-chips">
              {(['All', 'Pending', 'In Progress', 'Completed'] as (TaskStatus | 'All')[]).map(s => (
                <button key={s} className={`filter-chip ${statusFilter === s ? 'active' : ''}`}
                  onClick={() => setStatusFilter(s)}>{s}</button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <span className="filter-label">Priority</span>
            <div className="filter-chips">
              {(['All', 'High', 'Medium', 'Low'] as (Priority | 'All')[]).map(p => (
                <button key={p} className={`filter-chip ${priorityFilter === p ? 'active' : ''} ${p !== 'All' ? `priority-chip-${p.toLowerCase()}` : ''}`}
                  onClick={() => setPriorityFilter(p)}>{p}</button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <span className="filter-label">Category</span>
            <div className="filter-chips">
              {(['All', 'Development', 'Design', 'Testing', 'Meeting', 'Research', 'General', 'Other'] as (TaskCategory | 'All')[]).map(c => (
                <button key={c} className={`filter-chip ${categoryFilter === c ? 'active' : ''}`}
                  onClick={() => setCategoryFilter(c as TaskCategory | 'All')}>{c}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Task list */}
      {loading ? (
        <div className="loading-grid">
          {[1, 2, 3, 4].map(i => <div key={i} className="issue-card-skeleton" style={{ height: 180 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        tasks.length === 0 ? (
          <EmptyState
            title={isToday ? 'No tasks for today' : `No tasks for ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
            description={isToday ? 'Start your day by adding tasks you want to accomplish.' : 'No tasks were recorded for this day.'}
            actionLabel={isToday ? 'Add First Task' : undefined}
            actionTo={isToday ? '/tasks/create' : undefined}
          />
        ) : (
          <EmptyState
            title="No tasks match"
            description="Try adjusting your filters or search query."
            actionLabel="Clear Filters"
            onAction={() => { setStatusFilter('All'); setPriorityFilter('All'); setCategoryFilter('All'); setSearch(''); }}
          />
        )
      ) : (
        <div className="task-list">
          {filtered.map(task => (
            <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} />
          ))}
        </div>
      )}
    </div>
  );
}
